import { vases, type Vase } from '../data/vases';
import {
  loadSiteContentForAdmin,
  resolveSiteContentDraft,
  resolveSiteContentPublished,
  SITE_CONTENT_PUBLIC_SELECT,
  type SiteContentRecord,
} from './siteContentDraft';
import { supabase } from './supabase';
import type { ColourCombination, SelectedVase } from '../types';

export const PRODUCT_SITE_CONTENT_KEY = 'products';

export type ProductRecordStatus = 'draft' | 'active' | 'archived';
export type ProductModuleKey = 'logistics' | 'advancedPricing' | 'seo' | 'commerce';
export type ProductCustomFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'boolean'
  | 'select'
  | 'multi-select'
  | 'asset';

export interface ProductCustomField {
  id: string;
  key: string;
  type: ProductCustomFieldType;
  value: unknown;
  options?: string[];
}

export interface ProductModuleState {
  logistics: boolean;
  advancedPricing: boolean;
  seo: boolean;
  commerce: boolean;
}

export interface ProductLogisticsModule {
  weight: string;
  weightUnit: string;
  width: string;
  height: string;
  depth: string;
  dimensionUnit: string;
  shippingClass: string;
  packagingNotes: string;
}

export interface ProductAdvancedPricingModule {
  salePriceAmount: number | null;
  priceLabel: string;
  taxClass: string;
  isPriceOnRequest: boolean;
}

export interface ProductSeoModule {
  seoTitle: string;
  seoDescription: string;
  searchKeywords: string[];
}

export interface ProductCommerceModule {
  requestEnabled: boolean;
  enquiryEnabled: boolean;
  leadTime: string;
  availabilityLabel: string;
  minOrderQuantity: number | null;
  maxOrderQuantity: number | null;
}

export interface ProductRecord extends SelectedVase {
  slug: string;
  status: ProductRecordStatus;
  title: string;
  description: string;
  featuredImage: string;
  featuredImageAssetId: string | null;
  galleryImages: string[];
  galleryImageAssetIds: string[];
  imageAlt: string;
  collectionId: string | null;
  categoryId: string | null;
  collection: string;
  category: string;
  priceAmount: number | null;
  currencyCode: string;
  sku: string;
  tags: string[];
  featured: boolean;
  sortOrder: number;
  updatedAt: string | null;
  modelId: number | null;
  defaultColourwayId: string | null;
  modules: ProductModuleState;
  logistics: ProductLogisticsModule;
  advancedPricing: ProductAdvancedPricingModule;
  seo: ProductSeoModule;
  commerce: ProductCommerceModule;
  customFields: ProductCustomField[];
}

export const DEFAULT_PRODUCT_MODULES: ProductModuleState = {
  logistics: false,
  advancedPricing: false,
  seo: false,
  commerce: false,
};

export const DEFAULT_PRODUCT_LOGISTICS: ProductLogisticsModule = {
  weight: '',
  weightUnit: 'kg',
  width: '',
  height: '',
  depth: '',
  dimensionUnit: 'cm',
  shippingClass: '',
  packagingNotes: '',
};

export const DEFAULT_PRODUCT_ADVANCED_PRICING: ProductAdvancedPricingModule = {
  salePriceAmount: null,
  priceLabel: '',
  taxClass: '',
  isPriceOnRequest: false,
};

export const DEFAULT_PRODUCT_SEO: ProductSeoModule = {
  seoTitle: '',
  seoDescription: '',
  searchKeywords: [],
};

export const DEFAULT_PRODUCT_COMMERCE: ProductCommerceModule = {
  requestEnabled: true,
  enquiryEnabled: true,
  leadTime: '',
  availabilityLabel: '',
  minOrderQuantity: null,
  maxOrderQuantity: null,
};

export function slugifyProductName(value: string) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback;
}

function asTrimmedString(value: unknown, fallback = '') {
  const next = asString(value, fallback).trim();
  return next.length > 0 ? next : fallback;
}

function asStringArray(value: unknown) {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
    .filter((entry) => entry.length > 0);
}

function asNumberOrNull(value: unknown) {
  if (value == null || value === '') return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
}

function asPositiveNumber(value: unknown, fallback: number) {
  return Number.isFinite(value) ? Number(value) : fallback;
}

function asOptionalPositiveNumber(value: unknown) {
  return Number.isFinite(value) && Number(value) > 0 ? Number(value) : null;
}

function normalizeProductStatus(value: unknown): ProductRecordStatus {
  return value === 'draft' || value === 'archived' ? value : 'active';
}

function normalizeProductModules(value: unknown): ProductModuleState {
  const record = asRecord(value);
  return {
    logistics: record?.logistics === true,
    advancedPricing: record?.advancedPricing === true,
    seo: record?.seo === true,
    commerce: record?.commerce === true,
  };
}

function normalizeProductCustomField(value: unknown, index: number): ProductCustomField | null {
  const record = asRecord(value);
  if (!record) return null;
  const key = asTrimmedString(record.key, '');
  if (!key) return null;
  const type = record.type;
  const normalizedType: ProductCustomFieldType =
    type === 'textarea' ||
    type === 'number' ||
    type === 'boolean' ||
    type === 'select' ||
    type === 'multi-select' ||
    type === 'asset'
      ? type
      : 'text';

  let normalizedValue: unknown = record.value;
  if (normalizedType === 'number') {
    normalizedValue = asNumberOrNull(record.value);
  } else if (normalizedType === 'boolean') {
    normalizedValue = record.value === true;
  } else if (normalizedType === 'multi-select') {
    normalizedValue = asStringArray(record.value);
  } else {
    normalizedValue = record.value ?? '';
  }

  return {
    id: asTrimmedString(record.id, `custom-field-${index + 1}`),
    key,
    type: normalizedType,
    value: normalizedValue,
    options:
      normalizedType === 'select' || normalizedType === 'multi-select'
        ? asStringArray(record.options)
        : undefined,
  };
}

function createProductRecordFromVase(vase: Vase, index: number): ProductRecord {
  const title = vase.name;
  const description = vase.description ?? '';
  const image = vase.image ?? '';

  return {
    id: vase.id,
    name: vase.name,
    slug: slugifyProductName(vase.name),
    status: 'active',
    title,
    description,
    featuredImage: image,
    featuredImageAssetId: null,
    galleryImages: image ? [image] : [],
    galleryImageAssetIds: [],
    imageAlt: `${vase.name} product image`,
    collectionId: null,
    categoryId: null,
    collection: '',
    category: '',
    priceAmount: null,
    currencyCode: 'GBP',
    sku: '',
    tags: [],
    featured: false,
    sortOrder: index,
    updatedAt: null,
    modelId: vase.id,
    modelPath: vase.modelPath ?? '',
    image,
    defaultColourwayId: null,
    modules: { ...DEFAULT_PRODUCT_MODULES },
    logistics: { ...DEFAULT_PRODUCT_LOGISTICS },
    advancedPricing: { ...DEFAULT_PRODUCT_ADVANCED_PRICING },
    seo: { ...DEFAULT_PRODUCT_SEO },
    commerce: { ...DEFAULT_PRODUCT_COMMERCE },
    customFields: [],
  };
}

export function getFallbackProductRecords() {
  return vases.map((vase, index) => createProductRecordFromVase(vase, index));
}

function normalizeProductRecord(
  value: unknown,
  fallback: ProductRecord,
  index: number
): ProductRecord {
  const record = asRecord(value);
  if (!record) return fallback;

  const name = asTrimmedString(record.name, fallback.name);
  const title = asTrimmedString(record.title, name);
  const description = asString(
    record.description,
    asString(record.shortDescription, asString(record.fullDescription, fallback.description))
  );
  const featuredImage = asString(record.featuredImage, asString(record.image, fallback.featuredImage));
  const featuredImageAssetId = asTrimmedString(record.featuredImageAssetId, '') || null;
  const galleryImages = asStringArray(record.galleryImages);
  const galleryImageAssetIds = asStringArray(record.galleryImageAssetIds);
  const imageAlt = asTrimmedString(record.imageAlt, title || `${name} product image`);
  const baseModules = normalizeProductModules(record.modules);
  const legacySku = asString(record.sku, '');
  const legacyTags = asStringArray(record.tags);
  const nextModules: ProductModuleState = {
    ...baseModules,
    advancedPricing: baseModules.advancedPricing || legacySku.trim().length > 0,
  };
  const logisticsRecord = asRecord(record.logistics);
  const advancedPricingRecord = asRecord(record.advancedPricing);
  const seoRecord = asRecord(record.seo);
  const commerceRecord = asRecord(record.commerce);
  const legacyPrice = asNumberOrNull(record.price);

  return {
    ...fallback,
    id: asPositiveNumber(record.id, fallback.id),
    name,
    slug: asTrimmedString(record.slug, slugifyProductName(name)),
    status: normalizeProductStatus(record.status),
    title,
    description,
    featuredImage,
    featuredImageAssetId,
    galleryImages:
      galleryImages.length > 0
        ? galleryImages
        : featuredImage
          ? [featuredImage]
          : fallback.galleryImages,
    galleryImageAssetIds:
      galleryImageAssetIds.length > 0
        ? galleryImageAssetIds
        : featuredImageAssetId
          ? [featuredImageAssetId]
          : fallback.galleryImageAssetIds,
    imageAlt,
    collectionId: asTrimmedString(record.collectionId, '') || null,
    categoryId: asTrimmedString(record.categoryId, '') || null,
    collection: asString(record.collection, fallback.collection),
    category: asString(record.category, fallback.category),
    priceAmount: asNumberOrNull(record.priceAmount) ?? legacyPrice ?? fallback.priceAmount,
    currencyCode: asTrimmedString(record.currencyCode, fallback.currencyCode || 'GBP'),
    sku: legacySku || asString(advancedPricingRecord?.sku, fallback.sku),
    tags: legacyTags,
    featured: record.featured === true || legacyTags.includes('featured') || fallback.featured,
    sortOrder: asPositiveNumber(record.sortOrder, fallback.sortOrder ?? index),
    updatedAt: asString(record.updatedAt, fallback.updatedAt ?? '') || null,
    modelId: asOptionalPositiveNumber(record.modelId) ?? fallback.modelId,
    modelPath: asString(record.modelPath, fallback.modelPath ?? ''),
    image: featuredImage || fallback.image,
    price: asNumberOrNull(record.priceAmount) ?? legacyPrice ?? fallback.priceAmount,
    defaultColourwayId: asTrimmedString(record.defaultColourwayId, '') || null,
    modules: nextModules,
    logistics: {
      weight: asString(logisticsRecord?.weight, fallback.logistics.weight),
      weightUnit: asTrimmedString(logisticsRecord?.weightUnit, fallback.logistics.weightUnit),
      width: asString(logisticsRecord?.width, fallback.logistics.width),
      height: asString(logisticsRecord?.height, fallback.logistics.height),
      depth: asString(logisticsRecord?.depth, fallback.logistics.depth),
      dimensionUnit: asTrimmedString(
        logisticsRecord?.dimensionUnit,
        fallback.logistics.dimensionUnit
      ),
      shippingClass: asString(logisticsRecord?.shippingClass, fallback.logistics.shippingClass),
      packagingNotes: asString(logisticsRecord?.packagingNotes, fallback.logistics.packagingNotes),
    },
    advancedPricing: {
      salePriceAmount:
        asNumberOrNull(advancedPricingRecord?.salePriceAmount) ??
        fallback.advancedPricing.salePriceAmount,
      priceLabel: asString(advancedPricingRecord?.priceLabel, fallback.advancedPricing.priceLabel),
      taxClass: asString(advancedPricingRecord?.taxClass, fallback.advancedPricing.taxClass),
      isPriceOnRequest:
        advancedPricingRecord?.isPriceOnRequest === true || fallback.advancedPricing.isPriceOnRequest,
    },
    seo: {
      seoTitle: asString(seoRecord?.seoTitle, fallback.seo.seoTitle),
      seoDescription: asString(seoRecord?.seoDescription, fallback.seo.seoDescription),
      searchKeywords: asStringArray(seoRecord?.searchKeywords),
    },
    commerce: {
      requestEnabled:
        commerceRecord?.requestEnabled === true || fallback.commerce.requestEnabled,
      enquiryEnabled:
        commerceRecord?.enquiryEnabled === true || fallback.commerce.enquiryEnabled,
      leadTime: asString(commerceRecord?.leadTime, fallback.commerce.leadTime),
      availabilityLabel: asString(
        commerceRecord?.availabilityLabel,
        fallback.commerce.availabilityLabel
      ),
      minOrderQuantity:
        asNumberOrNull(commerceRecord?.minOrderQuantity) ??
        fallback.commerce.minOrderQuantity,
      maxOrderQuantity:
        asNumberOrNull(commerceRecord?.maxOrderQuantity) ??
        fallback.commerce.maxOrderQuantity,
    },
    customFields: Array.isArray(record.customFields)
      ? record.customFields
          .map((entry, customFieldIndex) =>
            normalizeProductCustomField(entry, customFieldIndex)
          )
          .filter((entry): entry is ProductCustomField => Boolean(entry))
      : [],
  };
}

export function normalizeProductRecords(value: unknown) {
  const fallbackRecords = getFallbackProductRecords();
  const incoming = Array.isArray(value) ? value : [];
  const incomingById = new Map<number, unknown>();

  incoming.forEach((entry) => {
    const record = asRecord(entry);
    const id = record && Number.isFinite(record.id) ? Number(record.id) : null;
    if (id && id > 0) {
      incomingById.set(id, entry);
    }
  });

  const merged = fallbackRecords.map((fallback, index) =>
    normalizeProductRecord(incomingById.get(fallback.id), fallback, index)
  );

  incoming.forEach((entry, index) => {
    const record = asRecord(entry);
    const id = record && Number.isFinite(record.id) ? Number(record.id) : null;
    if (id && fallbackRecords.some((fallback) => fallback.id === id)) return;

    const syntheticFallback = createProductRecordFromVase(
      {
        id: id && id > 0 ? id : fallbackRecords.length + index + 1,
        name: asTrimmedString(record?.name, `Product ${fallbackRecords.length + index + 1}`),
        description: asString(
          record?.description,
          asString(record?.shortDescription, asString(record?.fullDescription, ''))
        ),
        image: asString(record?.featuredImage, asString(record?.image, '')),
        modelPath: asString(record?.modelPath, ''),
      },
      fallbackRecords.length + index
    );

    merged.push(normalizeProductRecord(entry, syntheticFallback, fallbackRecords.length + index));
  });

  return merged.sort((left, right) => {
    if ((left.sortOrder ?? 0) !== (right.sortOrder ?? 0)) {
      return (left.sortOrder ?? 0) - (right.sortOrder ?? 0);
    }
    return left.name.localeCompare(right.name);
  });
}

export function createBlankProductRecord(nextId: number) {
  const defaultModel = vases.find((entry) => entry.modelPath) ?? vases[0];
  return {
    ...createProductRecordFromVase(
      {
        id: nextId,
        name: `Product ${nextId}`,
        description: '',
        image: '',
        modelPath: defaultModel?.modelPath,
      },
      nextId
    ),
    slug: `product-${nextId}`,
    status: 'draft' as ProductRecordStatus,
    title: `Product ${nextId}`,
    modelId: defaultModel?.id ?? null,
  };
}

export function findProductRecordById(
  records: ProductRecord[] | null | undefined,
  productId: number | null | undefined
) {
  if (!Array.isArray(records) || !Number.isFinite(productId)) return null;
  return records.find((record) => record.id === Number(productId)) ?? null;
}

export function resolveProductRecordModel(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return null;
  const modelId =
    Number.isFinite(record.modelId) && Number(record.modelId) > 0
      ? Number(record.modelId)
      : Number(record.id);
  return vases.find((entry) => entry.id === modelId) ?? null;
}

export function resolveProductRecordModelPath(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return '';
  const directPath =
    typeof record.modelPath === 'string' && record.modelPath.trim().length > 0
      ? record.modelPath.trim()
      : '';
  if (directPath) return directPath;
  return resolveProductRecordModel(record)?.modelPath ?? '';
}

export function resolveProductRecordTitle(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return '';
  const title =
    typeof record.title === 'string' && record.title.trim().length > 0
      ? record.title.trim()
      : '';
  return title || record.name;
}

export function resolveProductRecordDescription(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return '';
  const description =
    typeof record.description === 'string' && record.description.trim().length > 0
      ? record.description.trim()
      : '';
  return description;
}

export function resolveProductRecordFeaturedImage(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return '';
  const featuredImage =
    typeof record.featuredImage === 'string' && record.featuredImage.trim().length > 0
      ? record.featuredImage.trim()
      : '';
  const image =
    typeof record.image === 'string' && record.image.trim().length > 0
      ? record.image.trim()
      : '';
  return featuredImage || image || resolveProductRecordModel(record)?.image || '';
}

export function resolveProductRecordImageAlt(
  record: SelectedVase | ProductRecord | null | undefined
) {
  if (!record) return 'Product image';
  const imageAlt =
    typeof record.imageAlt === 'string' && record.imageAlt.trim().length > 0
      ? record.imageAlt.trim()
      : '';
  return imageAlt || resolveProductRecordTitle(record) || 'Product image';
}

export function resolveProductSelectionPayload(
  record: ProductRecord | SelectedVase | null | undefined
): SelectedVase | null {
  if (!record) return null;
  return {
    ...record,
    id: Number(record.id),
    name: resolveProductRecordTitle(record) || record.name,
    description: resolveProductRecordDescription(record),
    image: resolveProductRecordFeaturedImage(record),
    featuredImage: resolveProductRecordFeaturedImage(record),
    imageAlt: resolveProductRecordImageAlt(record),
    modelPath: resolveProductRecordModelPath(record),
    modelId:
      Number.isFinite(record.modelId) && Number(record.modelId) > 0
        ? Number(record.modelId)
        : Number(record.id),
    collectionId:
      typeof record.collectionId === 'string' && record.collectionId.trim().length > 0
        ? record.collectionId.trim()
        : null,
    categoryId:
      typeof record.categoryId === 'string' && record.categoryId.trim().length > 0
        ? record.categoryId.trim()
        : null,
    defaultColourwayId:
      typeof record.defaultColourwayId === 'string' && record.defaultColourwayId.trim().length > 0
        ? record.defaultColourwayId.trim()
        : null,
    priceAmount:
      typeof record.priceAmount === 'number' && Number.isFinite(record.priceAmount)
        ? record.priceAmount
        : null,
    currencyCode:
      typeof record.currencyCode === 'string' && record.currencyCode.trim().length > 0
        ? record.currencyCode.trim()
        : 'GBP',
    sku: typeof record.sku === 'string' ? record.sku : '',
    featured: record.featured === true,
    modules: record.modules,
    logistics: record.logistics,
    advancedPricing: record.advancedPricing,
    seo: record.seo,
    commerce: record.commerce,
    customFields: Array.isArray(record.customFields) ? record.customFields : [],
  };
}

export function resolveProductRecordsDraft(
  row: SiteContentRecord<ProductRecord[]> | null | undefined
) {
  return normalizeProductRecords(resolveSiteContentDraft(row, getFallbackProductRecords()));
}

export function resolveProductRecordsPublished(
  row: SiteContentRecord<ProductRecord[]> | null | undefined
) {
  return normalizeProductRecords(resolveSiteContentPublished(row, getFallbackProductRecords()));
}

export async function loadProductRecordsForAdmin() {
  const response = await loadSiteContentForAdmin<ProductRecord[]>(PRODUCT_SITE_CONTENT_KEY);
  if (response.error) {
    return {
      data: null,
      error: response.error,
      draft: getFallbackProductRecords(),
      published: getFallbackProductRecords(),
    };
  }

  return {
    data: response.data,
    error: null,
    draft: resolveProductRecordsDraft(response.data),
    published: resolveProductRecordsPublished(response.data),
  };
}

export async function loadPublishedProductRecords() {
  const response = await supabase
    .from('site_content')
    .select(SITE_CONTENT_PUBLIC_SELECT)
    .eq('key', PRODUCT_SITE_CONTENT_KEY)
    .maybeSingle<SiteContentRecord<ProductRecord[]>>();

  if (response.error) {
    return {
      data: null,
      error: response.error,
      products: getFallbackProductRecords(),
    };
  }

  return {
    data: response.data,
    error: null,
    products: resolveProductRecordsPublished(response.data),
  };
}

export function resolveDefaultColourwayOption(
  record: ProductRecord | SelectedVase | null | undefined,
  combinations: ColourCombination[]
) {
  if (!record) return null;
  const defaultId =
    typeof record.defaultColourwayId === 'string' && record.defaultColourwayId.trim().length > 0
      ? record.defaultColourwayId.trim()
      : null;
  if (defaultId) {
    return combinations.find((entry) => entry.id === defaultId) ?? null;
  }
  return combinations[0] ?? null;
}
